// src/Pages/conges/ValidateurConges.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { API_URL } from '../Config';
import { RefreshCw, Filter, Calendar, Clock, CheckCircle, AlertCircle, User, Mail } from 'lucide-react';

const ValidateurConges = () => {
  const [conges, setConges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchConges();
  }, []);

  const fetchConges = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      // Test different endpoints
      const endpoints = [
        '/validateur/conges',
        '/conges/validateur',
        '/conges?role=validateur',
        '/conges?statut=pending',
        '/conges'
      ];
      
      let response = null;
      
      for (const endpoint of endpoints) {
        try {
          response = await fetch(`${API_URL}${endpoint}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          });
          
          if (response.ok) {
            break;
          }
        } catch (err) {
          continue;
        }
      }
      
      if (!response || !response.ok) {
        response = await fetch(`${API_URL}/conges`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }

      const data = await response.json();
      
      let filteredData = data;
      
      if (data.data && Array.isArray(data.data)) {
        filteredData = data.data;
      } else if (Array.isArray(data)) {
        filteredData = data;
      } else if (data.conges && Array.isArray(data.conges)) {
        filteredData = data.conges;
      }
      
      if (!response.url.includes('validateur')) {
        filteredData = filteredData.filter(conge => 
          conge.status === 'cree'
        );
      }
      
      setConges(filteredData);
      
    } catch (error) {
      console.error('Erreur:', error);
      setError('Impossible de charger les demandes');
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), 'dd MMM yyyy', { locale: fr });
    } catch (error) {
      return dateString;
    }
  };

  const filteredConges = conges.filter(conge => {
    if (filter === 'all') return true;
    if (filter === 'pending') return conge.status === 'cree';
    return true;
  });

  const stats = {
    total: conges.length,
    pending: conges.filter(c => c.status === 'cree').length,
    exceptionnel: conges.filter(c => c.type === 'exceptionnel').length,
    normal: conges.filter(c => c.type === 'normal').length
  };

  if (loading) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-3 text-gray-500 text-sm">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Erreur de connexion</h3>
          <p className="text-gray-600 text-sm mb-6">{error}</p>
          <button
            onClick={fetchConges}
            className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Validation des congés</h1>
              <p className="text-gray-500 text-sm mt-1">Gestion des demandes en attente</p>
            </div>
            <button
              onClick={fetchConges}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md"
              title="Actualiser"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-white border border-gray-200 rounded p-4">
              <div className="text-lg font-semibold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-500">Total</div>
            </div>
            <div className="bg-white border border-yellow-100 rounded p-4 bg-yellow-50">
              <div className="text-lg font-semibold text-yellow-700">{stats.pending}</div>
              <div className="text-sm text-yellow-600">En attente</div>
            </div>
            <div className="bg-white border border-gray-200 rounded p-4">
              <div className="text-lg font-semibold text-gray-900">{stats.normal}</div>
              <div className="text-sm text-gray-500">Normaux</div>
            </div>
            <div className="bg-white border border-gray-200 rounded p-4">
              <div className="text-lg font-semibold text-gray-900">{stats.exceptionnel}</div>
              <div className="text-sm text-gray-500">Exceptionnels</div>
            </div>
          </div>

          {/* Filters */}
          <div className="mb-6">
            <div className="flex items-center text-sm text-gray-500 mb-3">
              <Filter className="w-4 h-4 mr-2" />
              Filtrer
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilter('pending')}
                className={`px-3 py-1.5 text-sm rounded-md ${
                  filter === 'pending' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white border border-gray-300 text-gray-700 hover:'
                }`}
              >
                En attente ({stats.pending})
              </button>
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1.5 text-sm rounded-md ${
                  filter === 'all' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white border border-gray-300 text-gray-700 hover:'
                }`}
              >
                Toutes ({stats.total})
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        {filteredConges.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {conges.length === 0 ? 'Aucune demande à valider' : 'Aucune demande correspondante'}
            </h3>
            <p className="text-gray-500 text-sm mb-6">
              {conges.length === 0 
                ? 'Toutes les demandes ont été traitées.' 
                : 'Modifiez vos filtres de recherche.'}
            </p>
            {filter !== 'all' && (
              <button
                onClick={() => setFilter('all')}
                className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:"
              >
                Voir toutes les demandes
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Demandeur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Période</th>
                    <th className="px6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durée</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredConges.map((conge) => (
                    <tr key={conge.id} className="hover:">
                      <td className="px-6 py-4">
                        <div>
                          <div className="flex items-center">
                            <User className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm font-medium text-gray-900">
                              {conge.user?.nom || conge.user?.name || 'Utilisateur'}
                            </span>
                          </div>
                          {conge.user?.email && (
                            <div className="flex items-center mt-1">
                              <Mail className="w-3 h-3 text-gray-400 mr-2" />
                              <span className="text-xs text-gray-500 truncate">
                                {conge.user.email}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {formatDate(conge.date_debut)} - {formatDate(conge.date_fin)}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center text-sm text-gray-900">
                          <Clock className="w-4 h-4 text-gray-400 mr-2" />
                          {conge.duree} jour{conge.duree > 1 ? 's' : ''}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2.5 py-0.5 text-xs rounded-full ${
                          conge.type === 'exceptionnel' 
                            ? 'bg-purple-100 text-purple-700' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {conge.type === 'exceptionnel' ? 'Exceptionnel' : 'Normal'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 text-xs rounded-full ${
                          conge.status === 'cree' 
                            ? 'bg-yellow-100 text-yellow-700 border border-yellow-200' 
                            : 'bg-green-100 text-green-700 border border-green-200'
                        }`}>
                          {conge.status === 'cree' ? (
                            <>
                              <AlertCircle className="w-3 h-3 mr-1" />
                              En attente
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Validé
                            </>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <Link
                            to={`/validateur/conge/${conge.id}/traiter`}
                            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Traiter
                          </Link>
                          <Link
                            to={`/conge/${conge.id}`}
                            className="px-3 py-1.5 text-sm border border-gray-300 text-gray-700 rounded hover:"
                          >
                            Détails
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Footer */}
            <div className=" px-6 py-3 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-gray-600 mb-2 sm:mb-0">
                  {filteredConges.length} demande{filteredConges.length !== 1 ? 's' : ''}
                </div>
                <div className="text-xs text-gray-500">
                  Délai de traitement : 48h maximum
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ValidateurConges;